import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface IUpdateManagementAccountIdRequest {
	/** The managed account (a Northwestern Mutual broker-dealer account). */
	accountId: string;
	/** The managing account's id; an empty string clears the link. */
	managementAccountId: string;
}

/**
 * Sets an account's managing account via `PATCH admin/account/{id}/managementAccountId` (the
 * react-query replacement for the MobX `AccountOperationsViewModel.saveManagementAccountId`). The
 * id rides as a query param with no body; the updated `Api.IAccount` is returned.
 *
 * This is a separate endpoint from the account PUT — the account save does not persist
 * `managementAccountId`, so the overview commits it alongside, the same way it handles next meeting.
 * Mutations have no MutationCache, so the error toast is wired here explicitly.
 */
export function useUpdateManagementAccountIdMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ accountId, managementAccountId }: IUpdateManagementAccountIdRequest) =>
			userSession.webServiceHelper.callAsync<Api.IAccount>(
				`admin/account/${encodeURIComponent(accountId)}/managementAccountId/?managementAccountId=${encodeURIComponent(
					managementAccountId
				)}`,
				'PATCH'
			),
		onError: () => toast.error('Failed to update the managing account. Please try again.'),
	});
}
